import { PageContainer } from "../../components/v2/PageContainer";
import { Header } from "../../components/v2/userManagment/Header";
import {
  AvailableTabs,
  UsersTabMenu,
} from "../../components/v2/userManagment/UsersTabMenu";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Backdrop, Box, Button, CircularProgress, Link, Typography } from "@mui/material";
import { UserSearchBox } from "../../components/v2/userManagment/UserSearchBox";
import { UsersTable } from "../../components/v2/userManagment/tables/UsersTable";
import { PaginationComponent } from "../../components/v2/userManagment/PaginationComponent";
import {
  ClassExtended,
  DbUser,
  InsightsCtx,
  InsightsView,
  LtiTools,
  UserCtx,
} from "../../interfaces";
import { UserContext } from "../../context/UserContext";
import { GridSortDirection, GridSortModel } from "@mui/x-data-grid";
import {
  School,
  ViewUserManagement,
  Courses,
  ViewLtiAssignmentMetadata,
} from "@backend-types";
import { useIsMounted } from "../../hooks/useIsMounted";
import { Role, SubRoles } from "../../enums";
import axios from "../../services/axios";
import { logger } from "../../services/logger";
import { ClassesTable } from "../../components/v2/userManagment/tables/ClassesTable";
import { CoursesTable } from "../../components/v2/userManagment/tables/CoursesTable";
import { SchoolsTable } from "../../components/v2/userManagment/tables/SchoolsTable";
import { AddNewItemBtn } from "../../components/v2/userManagment/AddNewItemBtn";
import { FilterButton } from "../../components/v3/userManagment/FilterButton";
import { ActiveFilter } from "../../components/v2/userManagment/FilterForm";
import { useDidMountEffect } from "../../hooks/useDidMount";
import { generateGtmEvent, sanitizeData } from "../../Helpers";
import { AlertState } from "../../types";
import { ErrorToast, SuccessToast, WarningToast } from "../../components/v2/utils/Toast";
import { BulkUserErrorModal } from "../../components/v2/userManagment/utils/BulkUserErrorModal";
import { InsightsContext } from "../../context/InsightsContext";
import { CustomChip } from "../../components/v2/utils/CustomChip";
import { LtiToolsTable } from "../../components/v2/userManagment/tables/LtiToolsTable";
import { useXLSXGenerator } from "../../hooks/useXLSXGenerator";
import { Helmet } from "react-helmet-async";
import { LtiAssignmentsTable } from "../../components/v2/userManagment/tables/LtiAssignmentsTable";
import { ALL_TEAM_EMAILS } from "../../constants";

export interface DataTablePaginationProps {
  totalResults: number;
  resultsPerPage: number;
  page: number;
  setPage: (value: number) => void;
}

export interface DataTableSortProps {
  field: string;
  sort: GridSortDirection;
  handleSort: (sortModel: GridSortModel) => void;
}

export interface UsersManagementProps {
  users?: ViewUserManagement[];
  currentUser: DbUser;
  componentType?: string;
  schools?: School[];
  classes?: ClassExtended[];
  tools?: LtiTools[];
  assignments?: ViewLtiAssignmentMetadata[];
  onDelete: (alert: AlertState | null) => void;
  pagination?: DataTablePaginationProps;
  sort?: DataTableSortProps;
  listContainsDisabledItems?: boolean;
  columnNames?: string[];
  heading?: string;
}

interface UsersPageProps {
  tab: number;
  columnNames?: string[];
  defaultSortField?: string;
  title?: string;
  description?: string;
  hideTabs?: boolean;
  hideHeader?: boolean;
  subHeading?: string;
  hideSearch?: boolean;
  hidePagination?: boolean;
  useUsersOverride?: boolean;
  /**
   * if true, query params will force get-users endpoint not to use the is_active filter
   * if false, will filter out archived users on tab 0 and include only archived users on tab 3
   */
  includeArchivedUsers?: boolean;
  insightsView?: InsightsView;
}

export type ModalState =
  | "single"
  | "bulk"
  | "class"
  | "school"
  | "ltitool"
  | "course"
  | "maintenanceBanner"
  | null;

export interface PrismaFilterQuery {
  AND: any[];
}

export const UsersPage = (props: UsersPageProps) => {
  let {
    tab,
    hideTabs = false,
    hideHeader = false,
    title = "User management",
    description = "Admins can edit user, class and school information on this page. Teachers have access to view information, but cannot edit student information.",
    hideSearch = false,
    hidePagination = false,
    useUsersOverride = false,
    includeArchivedUsers = false,
    defaultSortField = "first_name",
    insightsView,
  } = props;
  const queryParams = new URLSearchParams(window.location.search);
  const querySearch = queryParams.get("search") as string;
  const querySort = queryParams.get("sort") as string;
  const querySortDir = queryParams.get("sortdir") as string;
  const queryPage = Number(queryParams.get("page")) as number;

  const pathParams = useParams<{ class_id: string }>();
  const insightsClassId = !Number.isNaN(Number(pathParams.class_id))
    ? Number(pathParams.class_id)
    : undefined;

  const history = useHistory();
  const userCtx = useContext(UserContext) as UserCtx;
  const insightsCtx = useContext(InsightsContext) as InsightsCtx;

  const currentUser: DbUser = userCtx.dbUser;
  const [users, setUsers] = useState<ViewUserManagement[]>([]);
  const [schoolResults, setSchoolResults] = useState<School[]>([]);
  const [courseResults, setCourseResults] = useState<Courses[]>([]);
  const [ltiTools, setLtiTools] = useState<LtiTools[]>([]);
  const [ltiAssignments, setLtiAssignments] = useState<ViewLtiAssignmentMetadata[]>([]);
  const [classResults, setClassResults] = useState<ClassExtended[]>([]);
  const [archivedClassResults, setArchivedClassResults] = useState<ClassExtended[]>([]);
  const [totalResults, setTotalResults] = useState<number>(25);
  const [resultsPerPage, setResultsPerPage] = useState<number>(25);
  const [page, setPage] = useState<number>(queryPage ? queryPage : 1);
  const [fetchingData, setFetchingData] = useState(false);
  const isMounted = useIsMounted();
  const [searchText, setSearchText] = useState<string>(querySearch ? querySearch : "");
  const [sortField, setSortField] = useState(querySort ? querySort : defaultSortField);
  const columnNames = useRef<string[] | undefined>(props.columnNames);
  columnNames.current = props.columnNames;

  const [activeFilter, setActiveFilter] = useState<{ [key: string]: [] }>({});
  const [prismaFilter, setPrismaFilter] = useState<PrismaFilterQuery | null>(null);

  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [erroredList, setErroredList] = useState<string[]>([]);

  const checkSort = (string: string): GridSortDirection => {
    const sortDirections = ["asc", "desc"];
    return sortDirections.indexOf(string) !== -1
      ? (string as GridSortDirection)
      : ("asc" as GridSortDirection);
  };

  const [sortDir, setSortDir] = useState<GridSortDirection>(checkSort(querySortDir));
  const isReadOnlyAdmin = currentUser?.user_subrole?.some(
    (role) => role.sub_role === SubRoles.ADMIN_READ_ONLY
  );
  const displayAddNewItemBtn = currentUser?.role !== Role.teacher && !isReadOnlyAdmin; //only admin, super-admin, teacher see this page so in these roles only admin and super-admin can add new users
  const isSuperAdmin = currentUser?.role === Role.super;
  const displayDownloadBtn = [Role.super, Role.admin].includes(currentUser?.role);

  const [modalState, setModalState] = useState<ModalState>(null);

  const [hover, setHover] = useState(false);
  let availableTabs: AvailableTabs[] = [
    {
      label: "Users",
      id: "users",
      route: "/user-management",
      defaultSort: "first_name",
      heading: "Your users",
    },
    {
      label: "Classes",
      id: "Classes",
      route: "/class-management",
      defaultSort: "",
      heading: "Your classes",
    },
    {
      label: "Schools",
      id: "Schools",
      route: "/school-management",
      defaultSort: "",
      heading: "Your schools",
      isHidden: !isSuperAdmin,
    },
    {
      label: "Archived",
      id: "archived",
      route: "/archived-user",
      defaultSort: "full_name",
      heading: "Deactivated users",
      isHidden: false,
    },
    {
      label: "Archived Classes",
      id: "archived-classes",
      route: "/archived-class-management",
      defaultSort: "",
      heading: "Archived classes",
      isHidden: true,
    },
    {
      label: "Lti Tools",
      id: "lti-tools",
      route: "/lti-tools",
      defaultSort: "",
      heading: "LTI Tools",
      isHidden: !ALL_TEAM_EMAILS.includes(currentUser.email),
    },
    {
      label: "Courses",
      id: "courses",
      route: "/course-management",
      defaultSort: "",
      heading: "Your Courses",
      isHidden: !isSuperAdmin,
    },
    {
      label: "Lti Assignments",
      id: "lti-assignments",
      route: "/lti-assignments",
      defaultSort: "",
      heading: "LTI Assignments",
      isHidden: !isSuperAdmin,
    },
  ];

  useDidMountEffect(() => {
    const createPrismaFilterQuery = () => {
      let filterArray = Object.entries(activeFilter);
      console.log(activeFilter, filterArray);
      if (filterArray.length > 0) {
        // create prisma where query from activeFilter
        let prismaFilterQuery = { AND: [] } as PrismaFilterQuery;
        for (let i = 0; i < filterArray.length; i++) {
          let [key, value] = filterArray[i];
          // @ts-ignore
          if (Array.isArray(value) && value.length > 0) {
            prismaFilterQuery.AND.push({
              [key]: {
                // @ts-ignore
                in: value.map((item: ActiveFilter) => item.name),
              },
            }); // add empty object to AND array
          } else if (!Array.isArray(value) && value !== undefined) {
            prismaFilterQuery.AND.push({
              [key]: value,
            }); // add empty object to AND array
          }
        }
        setPrismaFilter(prismaFilterQuery);
      } else {
        setPrismaFilter(null);
      }
    };
    createPrismaFilterQuery();
  }, [activeFilter]);

  useDidMountEffect(() => {
    setActiveFilter({});
    setPrismaFilter({} as PrismaFilterQuery);
  }, [tab]);

  const getUsers = useCallback(
    async (
      search?: string,
      sort?: string,
      sortDir?: GridSortDirection,
      page?: number,
      filterQuery?: PrismaFilterQuery | null,
      disablePagination?: boolean
    ) => {
      setFetchingData(true);
      try {
        const result = await axios.post(
          "/users",
          {
            filter: filterQuery,
            textSearchFields: [...(columnNames.current ?? [])],
          },
          {
            params: {
              ...(search && search.trim() && { search: search?.trim() }),
              ...(sort && { sort }),
              ...(sortDir && { sortdir: sortDir }),
              ...(page && { page }),
              ...(tab === 3 && { archived_only: true }),
              ...(tab === 0 && includeArchivedUsers && { include_archived: true }),
              ...(insightsClassId && { insightsClassId: insightsClassId }), // when navigating from insights page overrides filter
              ...(disablePagination && { disablePagination }),
            },
          }
        );
        if (isMounted() && !disablePagination) {
          setUsers(result.data.users);
          setTotalResults(result.data.totalUsers);
          setResultsPerPage(result.data.resultsPerPage);
        } else if (disablePagination === true) {
          return result.data.users;
        }
      } catch (e) {
        logger.error(e);
      } finally {
        if (isMounted()) {
          setFetchingData(false);
        }
      }
    },
    [isMounted, tab, insightsClassId, includeArchivedUsers]
  );

  const getSchools = useCallback(
    async (search?: string, page?: number, disablePagination?: boolean) => {
      setFetchingData(true);
      try {
        const result = await axios.get("/schools", {
          params: {
            ...(search && search.trim() && { filter: search?.trim() }),
            ...(page && { page }),
            usermanagement: true,
            ...(disablePagination && { disablePagination }),
          },
        });
        if (isMounted() && !disablePagination) {
          setSchoolResults(result.data.results);
          setTotalResults(result.data.totalResults);
          setResultsPerPage(result.data.resultsPerPage);
        } else if (disablePagination === true) {
          return result.data.results;
        }
      } catch (e) {
        logger.error(e);
      } finally {
        if (isMounted()) {
          setFetchingData(false);
        }
      }
    },
    [isMounted]
  );

  const getCourses = useCallback(
    async (search?: string, page?: number, disablePagination?: boolean) => {
      setFetchingData(true);
      try {
        const result = await axios.get("/courses", {
          params: {
            ...(search && search.trim() && { filter: search?.trim() }),
            ...(page && { page }),
            ...(disablePagination && { disablePagination }),
            bypassCache: true,
          },
        });
        if (isMounted() && !disablePagination) {
          setCourseResults(result.data);
          setTotalResults(result.data.totalResults);
          setResultsPerPage(result.data.resultsPerPage);
        } else if (disablePagination === true) {
          return result.data.results;
        }
      } catch (e) {
        logger.error(e);
      } finally {
        if (isMounted()) {
          setFetchingData(false);
        }
      }
    },
    [isMounted]
  );

  const getClasses = useCallback(
    async (
      search?: string,
      page?: number,
      filterQuery?: PrismaFilterQuery | null,
      disablePagination?: boolean
    ) => {
      setFetchingData(true);
      try {
        const result = await axios.post(
          "/classes",
          {
            filter: filterQuery,
          },
          {
            params: {
              ...(search && search.trim() && { search: search?.trim() }),
              ...(page && { page }),
              usermanagement: true,
              version: "v2",
              ...(disablePagination && { disablePagination }),
            },
          }
        );
        if (isMounted() && !disablePagination) {
          setClassResults(result.data.results);
          setTotalResults(result.data.totalResults);
          setResultsPerPage(result.data.resultsPerPage);
        } else if (disablePagination === true) {
          return result.data.results;
        }
      } catch (e) {
        logger.error(e);
      } finally {
        if (isMounted()) {
          setFetchingData(false);
        }
      }
    },
    [isMounted]
  );

  const getArchivedClasses = useCallback(
    async (
      search?: string,
      sort?: string,
      sortDir?: GridSortDirection,
      page?: number,
      filterQuery?: PrismaFilterQuery | null,
      disablePagination?: boolean
    ) => {
      setFetchingData(true);
      try {
        const result = await axios.post(
          "/classes",
          {
            filter: filterQuery,
          },
          {
            params: {
              ...(search && search.trim() && { search: search?.trim() }),
              ...(page && { page }),
              ...(sort && { sort }),
              ...(sortDir && { sortdir: sortDir }),
              usermanagement: true,
              archived_only: true,
              version: "v2",
              ...(disablePagination && { disablePagination }),
            },
          }
        );
        if (isMounted() && !disablePagination) {
          setArchivedClassResults(result.data.results);
          setTotalResults(result.data.totalResults);
          setResultsPerPage(result.data.resultsPerPage);
        } else if (disablePagination === true) {
          return result.data.results;
        }
      } catch (e) {
        logger.error(e);
      } finally {
        if (isMounted()) {
          setFetchingData(false);
        }
      }
    },
    [isMounted]
  );

  const getLtiTools = useCallback(async () => {
    setFetchingData(true);
    try {
      const result = await axios.get("/lti/1p3/tools");
      if (isMounted()) {
        console.log("lti tools", result.data);
        setLtiTools(result.data);
        setTotalResults(result.data.length);
        setResultsPerPage(result.data.length);
      }
    } catch (e) {
      logger.error(e);
    } finally {
      if (isMounted()) {
        setFetchingData(false);
      }
    }
  }, [isMounted]);

  const getLtiAssignments = useCallback(
    async (
      search?: string,
      sort?: string,
      sortDir?: GridSortDirection,
      page?: number,
      filterQuery?: PrismaFilterQuery | null,
      disablePagination?: boolean
    ) => {
      setFetchingData(true);
      try {
        console.log(search, sort, sortDir, page, filterQuery, disablePagination);
        const result = await axios.post(
          "/lti/1p3/assignments",
          {
            filter: filterQuery,
            textSearchFields: [...(columnNames.current ?? [])],
          },
          {
            params: {
              ...(search && search.trim() && { search: search?.trim() }),
              ...(sort && { sort }),
              ...(sortDir && { sortdir: sortDir }),
              ...(page && { page }),
              ...(insightsClassId && { insightsClassId: insightsClassId }), // when navigating from insights page overrides filter
              ...(disablePagination && { disablePagination }),
            },
          }
        );
        if (isMounted() && !disablePagination) {
          setLtiAssignments(result.data.assignments);
          setTotalResults(result.data.totalAssignments);
          setResultsPerPage(result.data.resultsPerPage);
        } else if (disablePagination === true) {
          return result.data.assignments;
        }
      } catch (e) {
        logger.error(e);
      } finally {
        if (isMounted()) {
          setFetchingData(false);
        }
      }
    },
    [isMounted, insightsClassId]
  );

  const getUserOverrides = useCallback(() => {
    if (insightsView === "student") {
      const userSchool =
        insightsCtx.userInfo?.user_school && insightsCtx.userInfo?.user_school.length > 0
          ? insightsCtx.userInfo?.user_school[0].school
          : undefined;
      setUsers(
        insightsCtx.userInfo
          ? [
              {
                ...insightsCtx.userInfo,
                school_name: userSchool?.name ?? "",
                grade: null,
                is_demo: false,
                is_tester_class: [false],
                is_tester_coalesced: false,
                district: userSchool?.district ?? "",
                subject: [""],
                period_block: [""],
                subject_period_combined: [""],
                subject_str: "",
                period_block_str: "",
                subject_period_combined_str: "",
                class_start_date: [new Date()],
                class_start_date_str: "",
                teacher_name: [""],
                teacher_email: [""],
                teacher_email_str: "",
                teacher_name_str: "",
                created_at: new Date(),
                created_at_str: "",
                updated_at: new Date(),
                last_access_at: new Date(),
                last_access_at_str: "",
                school_id: null,
                class_id: [-1],
                teacher_id: [""],
                feature_ids: "",
                observer_ids: "",
                class_observer_ids: "",
                student_observer_ids: "",
                disabled_reason: null,
              },
            ]
          : []
      );
    } else if (insightsView === "class") {
      // tbd
    }
  }, [insightsView, insightsCtx.userInfo]);

  const refreshTableRef = useRef(() => {});

  useEffect(() => {
    refreshTableRef.current = () => {
      switch (tab) {
        case 7:
          getLtiAssignments(searchText, sortField, sortDir, page, prismaFilter);
          break;
        case 5:
          // get lti tools
          getLtiTools();
          break;
        case 3:
          // get archived users
          !useUsersOverride
            ? getUsers(searchText, sortField, sortDir, page, prismaFilter)
            : getUserOverrides();
          break;
        case 2:
          // get schools
          getSchools(searchText, page);
          break;
        case 1:
          // get classes
          getClasses(searchText, page, prismaFilter);
          break;
        case 4:
          // get archived classes
          getArchivedClasses(searchText, sortField, sortDir, page, prismaFilter);
          break;
        case 6:
          getCourses(searchText, page);
          break;
        case 0:
        default:
          !useUsersOverride
            ? getUsers(searchText, sortField, sortDir, page, prismaFilter)
            : getUserOverrides();
          break;
      }
    };
  }, [
    getUserOverrides,
    getArchivedClasses,
    useUsersOverride,
    getUsers,
    getSchools,
    getClasses,
    getCourses,
    getLtiTools,
    getLtiAssignments,
    searchText,
    sortField,
    sortDir,
    page,
    tab,
    prismaFilter,
  ]);

  useEffect(() => {
    // prismaFilter updates frequently within a short time frame
    // Debounce useEffect to ensures refreshTableRef.current() is not called excessively
    const timeout = setTimeout(() => {
      refreshTableRef.current();
      history.push({
        search: new URLSearchParams({
          ...(searchText && { search: searchText }),
          ...((tab === 0 || tab === 3 || tab === 4) && sortField && { sort: sortField }),
          ...((tab === 0 || tab === 3 || tab === 4) &&
            sortDir && { sortdir: `${sortDir}` }),
          ...(page && { page: `${page}` }),
        }).toString(),
      });
    }, 100);
    return () => clearTimeout(timeout);
  }, [searchText, sortField, sortDir, page, history, tab, prismaFilter]);

  const handleSubmit = (alert: AlertState | null = null) => {
    refreshTableRef.current();
    if (alert) {
      if (alert.severity === "success") {
        SuccessToast(alert.message);
      } else if (alert.severity === "error") {
        if (alert.meta && alert.meta.location === "AddBulkUsers") {
          setErroredList(alert.meta.error);
          setErrorModalOpen(true);
          return;
        }
        ErrorToast(alert.message);
      } else {
        WarningToast(alert.message);
      }
    }
  };

  const handleSearch = (searchQuery: string) => {
    if (searchQuery === "") {
    } else {
      HandleDataLayer(
        "search",
        "type",
        "onenter",
        "main search",
        "main content",
        props.subHeading ?? availableTabs[tab].heading,
        searchQuery
      );
    }
    setSearchText(searchQuery);
    if (
      Object.entries(activeFilter).reduce((acc, curr) => {
        return acc + curr[1].length;
      }, 0) > 0
    ) {
      //setActiveFilter({});
    }
    setPage(1);
  };

  const handleClear = () => {
    setSearchText("");
    setActiveFilter({});
    setPrismaFilter({} as PrismaFilterQuery);
    setPage(1);
    history.push({
      search: undefined,
    });
  };

  const handleChangeTab = (
    event: React.SyntheticEvent,
    newTabPath: string,
    newTabSort: string
  ) => {
    handleClear();
    setSortField(newTabSort);
    history.push(newTabPath);
  };

  const handleSort = React.useCallback((sortModel: GridSortModel) => {
    const field = sortModel[0]?.field || "";
    const sort = sortModel[0]?.sort || "";
    setSortField(field);
    setSortDir(sort as GridSortDirection);
  }, []);

  const handleChangePage = (event: React.ChangeEvent<unknown>, newPage: number) => {
    HandleDataLayer(
      "select",
      "click",
      "onclick",
      "pagination",
      "main content",
      props.subHeading ?? availableTabs[tab].heading,
      `page ${newPage}`
    );
    setPage(newPage);
  };

  const HandleDataLayer = (
    event: string,
    action: string,
    name: string,
    type: string,
    region: string,
    section: string,
    text: string
  ) => {
    generateGtmEvent({
      event: event,
      action: action,
      name: name,
      type: type,
      region: region,
      section: section,
      text: text,
    });
  };

  const generateXLSX = useXLSXGenerator();
  const handleDownload = async (tab: Number, jsonData: any[]) => {
    try {
      let modifiedDetails: any[] = [];
      let fileName: string = "";
      switch (tab) {
        case 0:
          modifiedDetails = jsonData.map((item) => {
            const data: any = {
              "First Name": item.first_name,
              "Last Name": item.last_name,
              Name: item.full_name,
              Email: item.email,
              Role: item.role,
              School: item.school_name,
              Classes: item.subject_period_combined_str,
              Teachers: item.teacher_name_str,
              Created: item.created_at_str,
              "Last log in": item.last_access_at_str,
            };
            return sanitizeData(data);
          });
          fileName = "Users_list";
          break;
        case 1:
          modifiedDetails = jsonData.map((item) => {
            const data: any = {
              "Course Name": item.course_name,
              "Teacher Name": item.teacher_name,
              "School Name": item.school_name,
              "Period/Block": item.period_block,
              "Students Count": item.student_count,
              "Start Date": item.start_date,
              "End Date": item.end_date,
            };
            return sanitizeData(data);
          });
          fileName = "Classes_list";
          break;
        case 2:
          modifiedDetails = jsonData.map((item) => {
            const data: any = {
              "School Name": item.name,
              District: item.district,
            };
            return sanitizeData(data);
          });
          fileName = "Schools_list";
          break;
        case 3:
          modifiedDetails = jsonData.map((item) => {
            const data: any = {
              "First Name": item.first_name,
              "Last Name": item.last_name,
            };
            return sanitizeData(data);
          });
          fileName = "Archived_list";
          break;
        case 7:
          modifiedDetails = jsonData.map((item) => {
            const data: any = {
              "Class ID": item.class_id,
              "Resource ID": item.resource_id,
              "Deeplink Asset Type": item.deeplink_asset_type,
              "Canvas Due Date": item.canvas_assignment_due_date,
              "Canvas Assignment Name": item.canvas_assignment_name,
              "Class Subject": item.class_subject,
              "Section SLN": item.section_sln,
              "Canvas Section #": item.canvas_section_number,
              "Grading Method": item.grading_method,
              "Grading Trigger": item.grading_trigger,
              "Late Penalty": item.late_penalty,
              "Max Days Late": item.max_days_late,
              "Is Synced": item.is_synced,
              "Canvas LP ID": item.canvas_lp_id,
              "Canvas LP Interval": item.canvas_lp_interval,
              "LP Late Deduction": item.canvas_lp_late_deduction,
              "LP Missing Deduction": item.canvas_lp_missing_deduction,
              "LP Min %": item.canvas_lp_min_percent,
              "LP Late Enabled": item.canvas_lp_late_enabled,
              "LP Missing Enabled": item.canvas_lp_missing_enabled,
              "LP Min % Enabled": item.canvas_lp_min_percent_enabled,
              "Canvas Term Name": item.canvas_term_name,
              "LTI Daily Late Penalty": item.lti_daily_late_penalty,
              "LTI Block Past Due Days": item.lti_block_past_due_days,
              "Block Past Due Passback": item.block_past_due_grade_passback,
              "LTI Grading Method": item.lti_grading_method,
              "LTI Grading Trigger": item.lti_grading_trigger,
            };
            return sanitizeData(data);
          });
          fileName = "Assignment_list";
          break;
        default:
          modifiedDetails = [];
      }

      generateXLSX({
        fileName,
        sheets: [
          {
            name: fileName,
            header: ["heading1"],
            data: modifiedDetails,
          },
        ],
      });
    } catch (error) {}
  };

  return (
    // data-sentry-block: admin content is redacted (empty box) in Sentry Session
    // Replay recordings while keeping one continuous replay session per user
    <div data-sentry-block>
      <Helmet>
        <title>Management | Digit</title>
      </Helmet>
      {!hideHeader && <Header title={title} description={description} />}
      {!hideTabs && (
        <UsersTabMenu
          value={tab}
          availableTabs={availableTabs}
          handleChange={handleChangeTab}
        />
      )}
      <PageContainer>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "32px",
            marginTop: "-2px",
          }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}>
              <Typography
                sx={{ fontSize: hideTabs ? "40px" : "28px" }}
                variant="h4"
                component={"span"}>
                {props.subHeading ?? availableTabs[tab].heading}
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                {displayDownloadBtn && (
                  <>
                    <Button
                      disableRipple={true}
                      sx={{ padding: "12px 16px" }}
                      variant="outlined"
                      size="large"
                      disableElevation
                      onMouseEnter={() => setHover(true)}
                      onMouseLeave={() => setHover(false)}
                      onClick={async () => {
                        try {
                          let dataToDownload;
                          switch (tab) {
                            case 0:
                              dataToDownload = await getUsers(
                                searchText,
                                sortField,
                                sortDir,
                                page,
                                prismaFilter,
                                true
                              );
                              break;

                            case 1:
                              dataToDownload = await getClasses(
                                searchText,
                                page,
                                prismaFilter,
                                true
                              );
                              break;

                            case 2:
                              dataToDownload = await getSchools(searchText, page, true);
                              break;

                            case 3:
                              dataToDownload = await getUsers(
                                searchText,
                                sortField,
                                sortDir,
                                page,
                                prismaFilter,
                                true
                              );
                              break;

                            case 6:
                              dataToDownload = await getCourses(searchText, page, true);
                              break;

                            case 7:
                              dataToDownload = await getLtiAssignments(
                                searchText,
                                sortField,
                                sortDir,
                                page,
                                prismaFilter,
                                true
                              );
                              break;

                            default:
                              break;
                          }
                          handleDownload(tab, dataToDownload);
                        } catch (e) {
                          ErrorToast("Error: No summary available to download");
                        }
                      }}
                      endIcon={
                        hover ? (
                          <i className="fa-solid fa-download fa-fw"></i>
                        ) : (
                          <i className="fa-regular fa-download fa-fw"></i>
                        )
                      }>
                      Download
                    </Button>
                  </>
                )}
                {displayAddNewItemBtn && !hideTabs && (
                  <AddNewItemBtn
                    setModalState={setModalState}
                    modalState={modalState}
                    currentUser={currentUser}
                    handleSubmit={handleSubmit}
                    sectionName={props.subHeading ?? availableTabs[tab].heading}
                  />
                )}
              </Box>
            </Box>
          </Box>
          {!hideSearch && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}>
              <UserSearchBox
                searchText={searchText}
                onSubmit={handleSearch}
                onCloseClick={() => {
                  HandleDataLayer(
                    "link",
                    "click",
                    "onclick",
                    "internal link",
                    "main content",
                    props.subHeading ?? availableTabs[tab].heading,
                    "close cross"
                  );
                }}
              />
              {(currentUser.role === Role.super || currentUser.role === Role.admin) &&
                tab !== 2 && (
                  <FilterButton
                    currentUser={currentUser}
                    activeFilters={activeFilter}
                    currentTab={tab}
                    setActiveFilters={setActiveFilter}
                    onClick={() => {
                      HandleDataLayer(
                        "modal",
                        "open",
                        "onclick",
                        "click",
                        "main content",
                        props.subHeading ?? availableTabs[tab].heading,
                        "filter"
                      );
                    }}
                  />
                )}
              <PaginationComponent
                page={page}
                totalResults={totalResults}
                resultsPerPage={resultsPerPage}
                handlePageChange={handleChangePage}
              />
            </Box>
          )}
          {Object.entries(activeFilter).reduce((acc, curr) => {
            return acc + curr[1].length;
          }, 0) > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "8px",
              }}>
              {Object.entries(activeFilter).map((item) => {
                return item[1].map((filter: any) => {
                  return (
                    <CustomChip
                      label={filter.name.replace(/\w\S*/g, function (txt: string) {
                        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
                      })}
                      onClose={() => {
                        setActiveFilter((prevState) => {
                          let newActiveFilter = { ...prevState };
                          // @ts-ignore
                          newActiveFilter[item[0]] = newActiveFilter[item[0]].filter(
                            (activeFilter: any) => {
                              return activeFilter.name !== filter.name;
                            }
                          );
                          return newActiveFilter;
                        });
                      }}
                      showAvatar={false}
                    />
                  );
                });
              })}
              <Link onClick={() => setActiveFilter({})} variant="body1">
                Clear all
              </Link>
            </Box>
          )}
          {tab === 0 && (
            <UsersTable
              columnNames={props.columnNames}
              heading={props.subHeading ?? availableTabs[tab].heading}
              users={users}
              currentUser={currentUser}
              onDelete={handleSubmit}
              sort={{
                handleSort,
                sort: sortDir,
                field: sortField,
              }}
              pagination={{
                totalResults,
                resultsPerPage,
                page,
                setPage,
              }}
            />
          )}
          {tab === 1 && (
            <ClassesTable
              classes={classResults}
              currentUser={currentUser}
              onDelete={handleSubmit}
              sort={{
                handleSort,
                sort: sortDir,
                field: sortField,
              }}
              pagination={{
                totalResults,
                resultsPerPage,
                page,
                setPage,
              }}
            />
          )}
          {tab === 2 && (
            <SchoolsTable
              schools={schoolResults}
              currentUser={currentUser}
              onDelete={handleSubmit}
              sort={{
                handleSort,
                sort: sortDir,
                field: sortField,
              }}
              pagination={{
                totalResults,
                resultsPerPage,
                page,
                setPage,
              }}
            />
          )}
          {tab === 3 && (
            <UsersTable
              columnNames={props.columnNames}
              heading={props.subHeading ?? availableTabs[tab].heading}
              listContainsDisabledItems={true}
              users={users}
              currentUser={currentUser}
              onDelete={handleSubmit}
              sort={{
                handleSort,
                sort: sortDir,
                field: sortField,
              }}
              pagination={{
                totalResults,
                resultsPerPage,
                page,
                setPage,
              }}
            />
          )}
          {tab === 4 && (
            <ClassesTable
              variant="table"
              classes={archivedClassResults}
              currentUser={currentUser}
              onDelete={handleSubmit}
              sort={{
                handleSort,
                sort: sortDir,
                field: sortField,
              }}
              pagination={{
                totalResults,
                resultsPerPage,
                page,
                setPage,
              }}
            />
          )}
          {/* LTI Tools */}
          {tab === 5 && (
            <LtiToolsTable
              tools={ltiTools}
              currentUser={currentUser}
              onDelete={handleSubmit}
              sort={{
                handleSort,
                sort: sortDir,
                field: sortField,
              }}
              pagination={{
                totalResults,
                resultsPerPage,
                page,
                setPage,
              }}
            />
          )}
          {/* LTI Assignments */}
          {isSuperAdmin && tab === 7 && (
            <LtiAssignmentsTable
              assignments={ltiAssignments}
              currentUser={currentUser}
              onDelete={handleSubmit}
              sort={{
                handleSort,
                sort: sortDir,
                field: sortField,
              }}
              pagination={{
                totalResults,
                resultsPerPage,
                page,
                setPage,
              }}
            />
          )}
          {/* Archived classes */}
          {tab === 6 && (
            <CoursesTable
              courses={courseResults}
              currentUser={currentUser}
              onDelete={handleSubmit}
              sort={{
                handleSort,
                sort: sortDir,
                field: sortField,
              }}
              pagination={{
                totalResults,
                resultsPerPage,
                page,
                setPage,
              }}
            />
          )}
          {!hidePagination && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginTop: "2rem",
              }}>
              <PaginationComponent
                page={page}
                totalResults={totalResults}
                resultsPerPage={resultsPerPage}
                handlePageChange={handleChangePage}
              />
            </Box>
          )}
        </Box>
      </PageContainer>
      {fetchingData && (
        <Backdrop open>
          <CircularProgress />
        </Backdrop>
      )}
      <BulkUserErrorModal
        open={errorModalOpen}
        failedUsers={erroredList}
        handleClose={() => setErrorModalOpen(false)}
      />
    </div>
  );
};
